import { AnyAction } from 'redux';
import { DocumentStatus, DocumentType } from '@synergy/models';
import { Media } from '@synergy/models';
import { PaginatedResponse } from '@synergy/api';
import { UPLOAD_FILE_REMOVE, UPLOAD_FILE_SUCCESS } from '@synergy/data-access';

import {
    ABORT_DEMAND_SUCCESS,
    DELETE_DEMAND,
    DUPLICATE_DEMAND,
    FINISH_DEMAND_SUCCESS,
    RECEIVE_DEMAND,
    RECEIVE_DEMANDS,
    REQUEST_DEMAND,
    REQUEST_DEMAND_END,
    REQUEST_DEMAND_START,
    RESET_DEMAND,
    RESET_DEMANDS,
    START_DEMAND_SUCCESS,
    UPDATE_DEMAND_ALERT,
} from './demand-actions';
import { Demand } from './models/demand';
import { DemandAccount } from './models/demand-account';

export interface MappedDemands {
    [id: number]: Demand;
}

export interface DemandReducerState {
    entity?: Demand;
    byId: MappedDemands;
    flat: PaginatedResponse<Demand>;
    requesting: boolean;
}

const initialDemandsReducerState: DemandReducerState = {
    entity: undefined,
    byId: {},
    flat: { limit: 10, page: 0, total: 0, items: [] },
    requesting: false,
};

export const mapDemandsById = (demands: Demand[]): MappedDemands => {
    // eslint-disable-next-line @typescript-eslint/ban-types
    return demands.reduce((accumulator: {}, demand) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (accumulator as any)[demand.id] = demand;
        return accumulator;
    }, {});
};

const uploadSuccessMediaDemandAccount = (
    documentType: DocumentType,
    documentId: number,
    media: Media,
    demand?: Demand
) => {
    if (!demand || documentType !== DocumentType.DemandAccount) {
        return demand;
    }

    const demandAccounts = [...demand.demandAccounts];
    const demandAccount = demandAccounts.find(account => account.id === documentId);
    if (!demandAccount) {
        return demand;
    }

    // Update media and return
    const index = demandAccounts.indexOf(demandAccount);
    const newDemandAccount = DemandAccount.fromPlainObject({
        ...demandAccount,
        media: [...demandAccount.media, media],
    });
    demandAccounts[index] = newDemandAccount;
    return Demand.fromPlainObject({ ...demand, demandAccounts });
};

const uploadRemoveMediaDemandAccount = (
    documentType: DocumentType,
    documentId: number,
    media: Media,
    demand?: Demand
) => {
    if (!demand || documentType !== DocumentType.DemandAccount) {
        return demand;
    }

    const demandAccounts = [...demand.demandAccounts];
    const demandAccount = demandAccounts.find(account => account.id === documentId);
    if (!demandAccount) {
        return demand;
    }

    // Get medias
    const mediaToModify = demandAccount.media.find(demandAccountMedia => demandAccountMedia.id === media.id);
    if (!mediaToModify) {
        return demand;
    }

    // Update offer, remove media
    const demandAccountIndex = demandAccounts.indexOf(demandAccount);
    const mediaIndex = demandAccount.media.indexOf(mediaToModify);
    const newMedia = [...demandAccount.media];
    newMedia.splice(mediaIndex, 1);
    const newDemandAccount = DemandAccount.fromPlainObject({ ...demandAccount, media: newMedia });
    demandAccounts[demandAccountIndex] = newDemandAccount;
    return Demand.fromPlainObject({ ...demand, demandAccounts });
};

export const demandsReducer = (
    state: DemandReducerState = initialDemandsReducerState,
    action: AnyAction
): DemandReducerState => {
    const { type, ...rest } = action;
    switch (type) {
        case RECEIVE_DEMANDS: {
            return {
                ...state,
                byId: mapDemandsById(action['demands'].items),
                flat: action['demands'],
            };
        }

        case RECEIVE_DEMAND: {
            // Add or update current demands array
            const paginatedDemands = { ...state.flat };
            const demands = [...paginatedDemands.items];
            const existingDemand = demands.find(demand => demand.id === action['entity'].id);
            if (existingDemand) {
                const index = demands.indexOf(existingDemand);
                demands[index] = action['entity'];
            } else {
                demands.unshift(action['entity']);
                // remove last one if surpassing page size
                if (demands.length > paginatedDemands.limit) {
                    demands.pop();
                }
            }
            paginatedDemands.items = demands;

            const entity = !state.entity || state.entity.id === action['entity'].id ? action['entity'] : state.entity;

            return {
                ...state,
                flat: paginatedDemands,
                byId: mapDemandsById(demands),
                entity,
                requesting: false,
            };
        }

        case RESET_DEMANDS: {
            return {
                ...state,
                byId: initialDemandsReducerState.byId,
                flat: initialDemandsReducerState.flat,
            };
        }

        case RESET_DEMAND: {
            return {
                ...state,
                entity: initialDemandsReducerState.entity,
            };
        }

        case DELETE_DEMAND: {
            const paginatedDemands = { ...state.flat };
            const demands = paginatedDemands.items.filter(demand => demand.id !== rest['demandId']);
            paginatedDemands.items = demands;
            paginatedDemands.total--;

            return {
                ...state,
                flat: paginatedDemands,
                byId: mapDemandsById(demands),
                entity: initialDemandsReducerState.entity,
            };
        }

        case UPLOAD_FILE_SUCCESS: {
            return {
                ...state,
                entity: uploadSuccessMediaDemandAccount(
                    rest['documentType'],
                    rest['documentId'],
                    rest['media'],
                    state.entity
                ),
            };
        }

        case UPLOAD_FILE_REMOVE: {
            return {
                ...state,
                entity: uploadRemoveMediaDemandAccount(
                    rest['documentType'],
                    rest['documentId'],
                    rest['media'],
                    state.entity
                ),
            };
        }

        case REQUEST_DEMAND:
        case DUPLICATE_DEMAND:
        case REQUEST_DEMAND_START: {
            return {
                ...state,
                requesting: true,
            };
        }

        case REQUEST_DEMAND_END: {
            return {
                ...state,
                requesting: false,
            };
        }

        case START_DEMAND_SUCCESS: {
            if (!state.entity) {
                return state;
            }

            return {
                ...state,
                entity: {
                    ...state.entity,
                    status: DocumentStatus.Ongoing,
                },
            };
        }

        case FINISH_DEMAND_SUCCESS: {
            const entity = state.entity;

            if (!entity) {
                return state;
            }

            return {
                ...state,
                entity: {
                    ...entity,
                    status: DocumentStatus.Completed,
                },
            };
        }

        case UPDATE_DEMAND_ALERT: {
            if (!state.entity) {
                return state;
            }

            return {
                ...state,
                entity: {
                    ...state.entity,
                    receiptAlert: rest['alert'],
                },
            };
        }

        case ABORT_DEMAND_SUCCESS: {
            const paginatedDemands = { ...state.flat }.items.map(demand => {
                if (demand.id === rest['demandId']) {
                    demand.status = DocumentStatus.Created;
                }

                return demand;
            });

            return {
                ...state,
                flat: {
                    ...state.flat,
                    items: paginatedDemands,
                },
            };
        }

        default: {
            return state;
        }
    }
};
